import { useCallback, useEffect, useState } from 'react';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { Button } from '../../../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../../components/ui/dialog';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';
import type { EnumLookupTarget, EnumValueSource } from '../../../../../types/electron';
import type {
  DeclareEnumColumnDialogMode,
  DeclareEnumColumnLookupIneligibleReason,
} from './declareEnumColumnDialogStore';
import { DeclareEnumLookupFields } from './DeclareEnumLookupFields';
import { DocsLinkButton } from '@/features/help-feedback';
import {
  classifyEnumValueError,
  type EnumDialogOperation,
} from '../../utils/enumValueErrorClassifier';
import { OperationErrorAlert } from '../feedback/OperationErrorAlert';

/**
 * Structured error the dialog renders via `OperationErrorAlert`. The
 * `operation` field lets the classifier pick the right user-friendly
 * headline — a failed sample reads differently from a failed declaration.
 */
export type DeclareEnumDialogError = {
  operation: EnumDialogOperation;
  message: string;
};

const TITLES: Record<DeclareEnumColumnDialogMode, string> = {
  native: 'Configure enum column',
  'check-constraint': 'Declare as enum (from constraint)',
  sampled: 'Declare as enum',
  eligible: 'This column looks like an enum',
  // Overridden at render time with the referenced table's name — this is the
  // fallback for the (impossible) lookup mode with no target.
  lookup: 'Show names from the referenced table',
};

/** Path-scoped variants — used when declaring a JSON property as an enum
 * (the column hosts the JSON; the path identifies the leaf or array). */
const PATH_TITLES: Record<DeclareEnumColumnDialogMode, string> = {
  // native / check-constraint don't apply to JSON paths today, but keep
  // safe fallbacks so the typing stays exhaustive.
  native: 'Configure JSON-path enum',
  'check-constraint': 'Declare JSON property as enum',
  sampled: 'Declare JSON property as enum',
  eligible: 'This JSON property looks like an enum',
  // Lookup enums are column-level only (a JSON path is never an FK in V1).
  lookup: 'Declare JSON property as enum',
};

const CONFIRM_LABELS: Record<DeclareEnumColumnDialogMode, string> = {
  native: 'Save metadata',
  'check-constraint': 'Declare as enum',
  sampled: 'Declare and use these values',
  eligible: 'Declare as enum',
  lookup: 'Declare and show names',
};

const PATH_CONFIRM_LABELS: Record<DeclareEnumColumnDialogMode, string> = {
  native: 'Save metadata',
  'check-constraint': 'Declare property as enum',
  sampled: 'Declare and use these values',
  eligible: 'Declare property as enum',
  lookup: 'Declare property as enum',
};

/**
 * Why an FK column can't be a lookup enum. Rendered muted under the
 * description so the user isn't left wondering where the names went.
 */
const LOOKUP_INELIGIBLE_COPY: Record<
  DeclareEnumColumnLookupIneligibleReason,
  string
> = {
  array:
    'Names from the referenced table aren’t available: this column holds an array of keys, not a single one.',
  polymorphic:
    'Names from the referenced table aren’t available: this column points at more than one table (or only under a condition).',
  'json-path':
    'Names from the referenced table aren’t available: the relationship on this column targets a JSON path, not the column itself.',
};

/** Map dialog mode to the EnumValueSource we'll persist on the declaration. */
export const modeToSource = (
  mode: DeclareEnumColumnDialogMode,
): EnumValueSource => {
  if (mode === 'native') return 'native';
  if (mode === 'check-constraint') return 'check-constraint';
  if (mode === 'lookup') return 'lookup';
  return 'sampled';
};

/**
 * `schema.table`, or just `table` on schemaless engines (Kusto reports `''`).
 * Presence-checked with `== null` + an explicit `''` test — never `!schema`.
 */
const qualifyTarget = (schema: string | undefined, table: string): string =>
  schema == null || schema === '' ? table : `${schema}.${table}`;

type DeclareEnumColumnDialogProps = {
  open: boolean;
  schema: string;
  table: string;
  column: string;
  dataType?: string;
  engine: DatabaseEngineId;
  mode: DeclareEnumColumnDialogMode;
  /** Pre-fetched preview values (e.g. native enum values or parsed CHECK list). */
  previewValues?: string[];
  /** True while a fetchEnumValues call is in flight (sampled / eligible modes). */
  previewLoading?: boolean;
  /** True if the preview hit the cap — refuse to confirm in this state. */
  previewTruncated?: boolean;
  /**
   * Structured error from the most-recent failed action (sample / declare /
   * dismiss). The dialog routes the raw message through
   * `classifyEnumValueError` to produce a user-friendly headline + an
   * explanation when we recognize the pattern (e.g. MSSQL OPENJSON compat
   * level), and surfaces the raw message in a collapsible "Show details"
   * section — replaces the previous wall-of-text error banner.
   */
  error?: DeclareEnumDialogError | null;
  /**
   * `asReferenceTable` (lookup mode only) — the user ticked "Mark <target> as
   * a reference table", so the host must persist the table-level flag INSTEAD
   * of a declaration on this column. Always `false` in every other mode.
   */
  onConfirm: (
    description: string,
    options: { asReferenceTable: boolean },
  ) => void | Promise<void>;
  onCancel: () => void;
  /** Eligible mode only: dismiss the enum glyph hint for this column.
   * Wiring to persistence happens in the caller (next task). */
  onDismissNotEnum?: () => void;
  /** When set, the dialog is declaring a JSON property at this path (e.g.
   * `$.status`, `$.tags[*]`) rather than the column itself. Drives all the
   * "property" vs "column" wording. */
  path?: string;
  /** Present iff `path` is set; informs leaf-vs-array description copy. */
  pathKind?: 'scalar' | 'array';
  /** Lookup mode: the FK target the key/label pairs are read from. */
  lookupTarget?: EnumLookupTarget;
  /** Lookup mode: labels aligned with `previewValues` (null = NULL label). */
  previewLabels?: (string | null)[];
  /** Lookup mode: the label-column dropdown's state (owned by the host). */
  labelColumnValue?: string;
  onLabelColumnChange?: (next: string) => void;
  labelColumnOptions?: ReadonlyArray<{ value: string; label: string }>;
  labelColumnLoading?: boolean;
  /** Lookup mode: flip to the plain sampled flow. */
  onUseSampledInstead?: () => void;
  /** Set on an FK column that CAN'T back a lookup enum — explains why. */
  lookupIneligibleReason?: DeclareEnumColumnLookupIneligibleReason;
};

export function DeclareEnumColumnDialog({
  open,
  schema,
  table,
  column,
  dataType,
  engine,
  mode,
  previewValues,
  previewLoading,
  previewTruncated,
  error,
  onConfirm,
  onCancel,
  onDismissNotEnum,
  path,
  pathKind,
  lookupTarget,
  previewLabels,
  labelColumnValue = '',
  onLabelColumnChange,
  labelColumnOptions = [],
  labelColumnLoading,
  onUseSampledInstead,
  lookupIneligibleReason,
}: DeclareEnumColumnDialogProps) {
  const isPath = !!path;
  const isLookup = mode === 'lookup' && !!lookupTarget;
  const titles = isPath ? PATH_TITLES : TITLES;
  const confirmLabels = isPath ? PATH_CONFIRM_LABELS : CONFIRM_LABELS;
  const [description, setDescription] = useState('');
  const [confirming, setConfirming] = useState(false);
  // Lookup mode only — "Mark <target> as a reference table". Local like
  // `description`: the host learns about it through `onConfirm`.
  const [referenceTable, setReferenceTable] = useState(false);

  useEffect(() => {
    if (open) {
      setDescription('');
      setConfirming(false);
      setReferenceTable(false);
    }
  }, [open]);

  const handleConfirm = useCallback(async () => {
    setConfirming(true);
    try {
      // The flag is a lookup-mode concept; every other mode always declares.
      await onConfirm(description.trim(), {
        asReferenceTable: mode === 'lookup' && referenceTable,
      });
    } finally {
      setConfirming(false);
    }
  }, [description, mode, onConfirm, referenceTable]);

  const classified = error
    ? classifyEnumValueError(error.message, error.operation, {
        isPath,
        pathKind,
      })
    : null;
  // Eligible / sampled modes block on truncation — column isn't actually
  // categorical, declaring it would set up a broken dropdown. Lookup mode
  // blocks on the same signal (the *referenced table* is too big) and also
  // when no label column resolves: a lookup enum with no names is pointless,
  // and keys-only declarations aren't offered in V1.
  const qualifiedTarget = lookupTarget
    ? qualifyTarget(lookupTarget.toSchema, lookupTarget.toTable)
    : '';
  // `'__none__'` mirrors `LooseFkDisplayColumnSelect`'s "(none)" sentinel —
  // inlined there and in the target picker for the same react-refresh reason.
  const missingLabelColumn =
    isLookup && (labelColumnValue === '' || labelColumnValue === '__none__');
  const truncationBlocked =
    (mode === 'sampled' || mode === 'eligible' || isLookup) &&
    Boolean(previewTruncated);
  const blocked = truncationBlocked || missingLabelColumn;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && onCancel()}
    >
      <DialogContent
        className="sm:max-w-md"
        data-testid="declare-enum-dialog"
        data-mode={mode}
      >
        <DialogHeader>
          <div className="flex items-center gap-1.5">
            <DialogTitle>
              {isLookup ? `Show names from ${qualifiedTarget}` : titles[mode]}
            </DialogTitle>
            <DocsLinkButton
              url={
                isPath
                  ? 'https://jamsql.com/docs/json-columns/'
                  : 'https://jamsql.com/docs/enum-columns/'
              }
              doc={isPath ? 'json-columns' : 'enum-columns'}
            />
          </div>
          <DialogDescription>
            <code
              className="font-mono text-xs"
              translate="no"
              data-testid="declare-enum-target"
            >
              {schema}.{table}.{column}
              {path ? ` · ${path}` : ''}
            </code>{' '}
            {isPath ? (
              mode === 'eligible' ? (
                <>
                  is a JSON {pathKind === 'array' ? 'array' : 'property'} whose
                  name suggests a finite, knowable set of values. Sampling
                  confirms the list.
                </>
              ) : (
                <>
                  Sample the JSON{' '}
                  {pathKind === 'array' ? 'array elements' : 'property'} to find
                  the distinct values. The 200-row cap protects against
                  accidentally treating a high-cardinality path as an enum.
                </>
              )
            ) : isLookup ? (
              <>
                references{' '}
                <span className="font-mono" translate="no">
                  {qualifiedTarget}
                </span>{' '}
                — its values and their names come from that table. Names show
                up in grids, filters and cell editing; the key is what stays
                stored, sorted, filtered and copied.
              </>
            ) : mode === 'native' ? (
              <>
                is a native enum on this {engine} connection — the values
                below come from the column type / catalog. Saving here
                stores user-supplied metadata (description, MetaInfo manager
                visibility).
              </>
            ) : mode === 'check-constraint' ? (
              <>
                has a CHECK constraint listing a fixed set of allowed
                values. Declare it as an enum to drive a dropdown filter
                sourced from the constraint.
              </>
            ) : mode === 'eligible' ? (
              <>
                looks like a finite, knowable set of values — short string
                type or a numeric column with a categorical name. Sampling
                the data confirms the list.
              </>
            ) : (
              <>
                Sample the data to find the distinct values. The 200-row
                cap protects against accidentally treating a high-cardinality
                column as an enum.
              </>
            )}
            {dataType && !isPath && (
              <>
                <br />
                <span
                  translate="no"
                  className="font-mono text-xs"
                  data-testid="declare-enum-datatype"
                >
                  {dataType}
                </span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {previewLoading ? (
          <div
            className="flex items-center gap-2 text-xs text-muted-foreground"
            data-testid="declare-enum-loading"
          >
            <RandomDotmSpinner size={14} />
            {isLookup ? 'Reading values from the referenced table…' : 'Sampling distinct values…'}
          </div>
        ) : previewValues && previewValues.length > 0 ? (
          <div
            className="space-y-1"
            data-testid="declare-enum-preview"
          >
            <p className="text-xs text-muted-foreground">
              {previewValues.length}{' '}
              {isLookup
                ? `row${previewValues.length === 1 ? '' : 's'} in ${qualifiedTarget}`
                : `distinct value${previewValues.length === 1 ? '' : 's'}`}
              {previewTruncated
                ? isLookup
                  ? ' (truncated — too many rows to use as an enum)'
                  : isPath
                    ? ' (truncated — this JSON path isn’t enum-like)'
                    : ' (truncated — column isn’t enum-like)'
                : ''}
            </p>
            <div
              // `min-w-0` lets this scroll box shrink to the dialog width instead
              // of letting a long chip's intrinsic min-content push the grid-sized
              // DialogContent past its max-width. `overflow-y-auto` (not
              // `overflow-auto`) plus wrapping chips means only vertical scroll.
              className="flex flex-wrap gap-1 max-h-32 min-w-0 overflow-y-auto rounded-md border bg-muted/30 p-2"
              data-testid="declare-enum-preview-chips"
            >
              {previewValues.map((v, i) => {
                // Lookup mode renders `key → label` pairs; a NULL label falls
                // back to the bare key (same chip as every other mode).
                const label = isLookup ? (previewLabels?.[i] ?? null) : null;
                return (
                  <span
                    key={v}
                    translate="no"
                    // `overflow-wrap:anywhere` wraps long, space-less values (e.g.
                    // `ActiveOrdersMessageHandler.OnDisconnected`) AND shrinks the
                    // chip's min-content so it can't force the grid-sized dialog
                    // past its max-width; `max-w-full` caps each chip at the box.
                    className="max-w-full [overflow-wrap:anywhere] rounded-full bg-background px-2 py-0.5 text-xs font-mono"
                    data-testid={label === null ? undefined : 'declare-enum-preview-pair'}
                  >
                    {v}
                    {label === null ? null : (
                      <>
                        <span className="text-muted-foreground"> → </span>
                        {label}
                      </>
                    )}
                  </span>
                );
              })}
            </div>
          </div>
        ) : null}

        {isLookup && lookupTarget && (
          <DeclareEnumLookupFields
            qualifiedTarget={qualifiedTarget}
            targetSchema={lookupTarget.toSchema}
            targetTable={lookupTarget.toTable}
            labelColumnValue={labelColumnValue}
            onLabelColumnChange={onLabelColumnChange ?? (() => {})}
            labelColumnOptions={labelColumnOptions}
            labelColumnLoading={labelColumnLoading}
            missingLabelColumn={missingLabelColumn}
            truncated={truncationBlocked}
            onUseSampledInstead={onUseSampledInstead ?? (() => {})}
            referenceTable={referenceTable}
            onReferenceTableChange={setReferenceTable}
          />
        )}

        {!isLookup && lookupIneligibleReason && (
          <p
            className="text-xs text-muted-foreground"
            data-testid="declare-enum-lookup-ineligible"
          >
            {LOOKUP_INELIGIBLE_COPY[lookupIneligibleReason]}
          </p>
        )}

        <div className="space-y-1">
          <Label htmlFor="declare-enum-description" className="text-xs">
            Description (optional)
          </Label>
          <Input
            id="declare-enum-description"
            data-testid="declare-enum-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Order lifecycle state"
          />
        </div>

        {classified && (
          <OperationErrorAlert
            testId="declare-enum-error"
            title={classified.title}
            description={classified.description}
            details={classified.details}
          />
        )}

        {/* `lookup` sits beside `eligible` here because the FK-aware `▾?`
            hint opens THIS dialog in lookup mode: a hinted column that the
            user rejects must stay dismissible, or the hint would be the one
            glyph in the app you cannot turn off. Never offered for `native` /
            `check-constraint` / `sampled` (nothing was proposed) or for a
            JSON path (dismissals are column-level). */}
        {(mode === 'eligible' || mode === 'lookup') && onDismissNotEnum && !isPath && (
          <p className="text-xs text-muted-foreground border-t pt-2">
            Doesn’t look like an enum?{' '}
            <button
              type="button"
              data-testid="declare-enum-not-an-enum"
              className="text-primary underline underline-offset-2 hover:no-underline"
              onClick={onDismissNotEnum}
            >
              Mark “<span translate="no">{column}</span>” as not an enum
            </button>
          </p>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onCancel}
            data-testid="declare-enum-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={confirming || blocked || previewLoading}
            data-testid="declare-enum-confirm"
          >
            {confirming && (
              <RandomDotmSpinner size={14} className="mr-2" />
            )}
            {confirming ? 'Declaring…' : confirmLabels[mode]}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
